import { useState, useEffect } from 'react';
import { Save, Eye, RotateCcw, AlertTriangle, Code2 } from 'lucide-react';

interface CustomTemplate {
  subject?: string;
  htmlBody?: string;
  textBody?: string;
  sections?: {
    greeting?: string;
    intro?: string;
    footer?: string;
  };
  lastModified?: string;
  modifiedBy?: string;
}

interface EmailTemplateSchema {
  type: string;
  name: string;
  description: string;
  variables: Array<any>;
  defaultSubject: string;
  defaultHtml: string;
  defaultText: string;
  sampleData: Record<string, any>;
}

interface FullTemplateEditorProps {
  schema?: EmailTemplateSchema;
  customTemplate: CustomTemplate | null;
  onSave: (template: CustomTemplate | null) => void;
  onPreview: (template?: CustomTemplate) => void;
  onReset: () => void;
  isSaving: boolean;
  isPreviewing: boolean;
}

export function FullTemplateEditor({
  schema,
  customTemplate,
  onSave,
  onPreview,
  onReset,
  isSaving,
  isPreviewing,
}: FullTemplateEditorProps) {
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [textBody, setTextBody] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'html' | 'text'>('html');

  // Load custom template data when it changes
  useEffect(() => {
    if (customTemplate) {
      setSubject(customTemplate.subject || '');
      setHtmlBody(customTemplate.htmlBody || '');
      setTextBody(customTemplate.textBody || '');
    } else if (schema) {
      // Load default templates
      setSubject(schema.defaultSubject || '');
      setHtmlBody(schema.defaultHtml || '');
      setTextBody(schema.defaultText || '');
    }
    setHasChanges(false);
  }, [customTemplate, schema]);

  const handleFieldChange = (field: string, value: string) => {
    setHasChanges(true);
    switch (field) {
      case 'subject':
        setSubject(value);
        break;
      case 'htmlBody':
        setHtmlBody(value);
        break;
      case 'textBody':
        setTextBody(value);
        break;
    }
  };

  const handleSave = () => {
    const template: CustomTemplate = {
      subject: subject || undefined,
      htmlBody: htmlBody || undefined,
      textBody: textBody || undefined,
    };
    onSave(template);
    setHasChanges(false);
  };

  const handlePreview = () => {
    const template: CustomTemplate = {
      subject: subject || undefined,
      htmlBody: htmlBody || undefined,
      textBody: textBody || undefined,
    };
    onPreview(template);
  };

  const handleLoadDefault = () => {
    if (schema && confirm('Load the default template? Your current changes will be lost.')) {
      setSubject(schema.defaultSubject);
      setHtmlBody(schema.defaultHtml);
      setTextBody(schema.defaultText);
      setHasChanges(true);
    }
  };

  if (!schema) {
    return (
      <div className="text-center text-muted-foreground py-8">
        Select a template to edit
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-warning-muted border border-warning-border text-warning-foreground rounded-lg p-4 flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-medium mb-1">Advanced Mode - Full Template Override</p>
          <p>
            You're editing the complete HTML and text templates. This gives you full control but requires knowledge of
            HTML and email design. Make sure to include all necessary variables using{' '}
            <code className="bg-warning-muted/50 px-1 py-0.5 rounded">{'{{variableName}}'}</code> syntax.
          </p>
        </div>
      </div>

      {/* Subject Line */}
      <div className="space-y-2">
        <label className="block text-sm font-medium">Subject Line</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => handleFieldChange('subject', e.target.value)}
          placeholder={schema.defaultSubject}
          className="w-full px-3 py-2 border rounded-md font-mono text-sm bg-background text-foreground"
        />
      </div>

      {/* HTML/Text Tabs */}
      <div className="border rounded-lg overflow-hidden">
        <div className="flex border-b bg-muted/30">
          <button
            onClick={() => setActiveTab('html')}
            className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'html'
                ? 'bg-background border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Code2 className="h-4 w-4" />
            HTML Template
          </button>
          <button
            onClick={() => setActiveTab('text')}
            className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'text'
                ? 'bg-background border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Plain Text
          </button>
        </div>

        <div className="p-4 bg-muted/10">
          {activeTab === 'html' ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">HTML Body</label>
                <button
                  onClick={handleLoadDefault}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Load default template
                </button>
              </div>
              <textarea
                value={htmlBody}
                onChange={(e) => handleFieldChange('htmlBody', e.target.value)}
                rows={20}
                className="w-full px-3 py-2 border rounded-md font-mono text-xs resize-y bg-background text-foreground"
                placeholder="Enter full HTML template..."
              />
              <p className="text-xs text-muted-foreground">
                Complete HTML email template. Use inline CSS for styling.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Plain Text Body</label>
              <textarea
                value={textBody}
                onChange={(e) => handleFieldChange('textBody', e.target.value)}
                rows={20}
                className="w-full px-3 py-2 border rounded-md font-mono text-xs resize-y bg-background text-foreground"
                placeholder="Enter plain text template..."
              />
              <p className="text-xs text-muted-foreground">
                Plain text version for email clients that don't support HTML.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Template Size Warning */}
      {(htmlBody.length + textBody.length) > 80000 && (
        <div className="bg-error-muted border border-error-border text-error-foreground rounded-lg p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            Template size: {Math.round((htmlBody.length + textBody.length) / 1024)}KB. Maximum is 100KB.
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Saving...' : 'Save Template'}
        </button>

        <button
          onClick={handlePreview}
          disabled={isPreviewing}
          className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
        >
          <Eye className="h-4 w-4" />
          {isPreviewing ? 'Loading...' : 'Preview'}
        </button>

        {customTemplate?.htmlBody && (
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 border border-error-border text-error rounded-md hover:bg-error-muted"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </button>
        )}

        {hasChanges && (
          <span className="text-sm text-muted-foreground ml-auto">
            Unsaved changes
          </span>
        )}
      </div>
    </div>
  );
}
